// ---------------------------------------------------------------------------
// LucidTopup - Service layer
// ---------------------------------------------------------------------------
// auth / profile / order / payment  -> ยิง PaymentBroker-Backend จริง
// แคตตาล็อก (เกม บัตร มือถือ สินค้า โปรโมชั่น) -> mock จนกว่า Backend จะมีตาราง
// ---------------------------------------------------------------------------
import { USE_MOCK_CATALOG, ENDPOINTS, PARTNER, CAPTCHA, PROCESS_TYPE, ORDER_TYPE } from "./config";
import { http, mockResponse, setToken, setStoredProfile, getStoredProfile, clearSession, unwrap, ApiError } from "./apiClient";
import * as M from "./mockData";

// --------------------------------------------------------------------------
// Auth
// --------------------------------------------------------------------------
export const authService = {
  // captchaToken = token จาก Cloudflare Turnstile (ใช้ master token เมื่อยังไม่ได้ต่อ widget)
  login: async ({ email, password, remember = false, captchaToken }) => {
    const result = await http.post(ENDPOINTS.login, {
      email,
      password: btoa(password),
      remember,
      token: captchaToken || CAPTCHA.masterToken,
    });
    if (!result.status) throw new ApiError(result.message, result);

    setToken(result.data.token);
    setStoredProfile({ ...result.data, password: "" });
    window.dispatchEvent(new Event("lt-auth-change"));
    return result.data;
  },

  register: async ({ fullname, phone, email, password, address }) => {
    const result = await http.post(ENDPOINTS.register, {
      name_th: fullname,
      name_en: fullname,
      phone,
      email,
      password,
      company_address: address,
    });
    if (!result.status) throw new ApiError(result.message, result);
    return result.data;
  },

  forgotPassword: async (email) => {
    const result = await http.post("v1/public/reset-password", { email });
    if (!result.status) throw new ApiError(result.message, result);
    return result.data;
  },

  logout: async () => {
    try {
      await http.get(ENDPOINTS.logout);
    } catch (e) {
      /* ignore */
    }
    clearSession();
    return { success: true };
  },

  currentProfile: () => getStoredProfile(),
  isLoggedIn: () => !!getStoredProfile(),
};

// --------------------------------------------------------------------------
// Profile
// --------------------------------------------------------------------------
// map response ของ Backend ให้เป็นรูปแบบที่หน้าเว็บใช้
const mapProfile = (raw) => {
  if (!raw) return null;
  return {
    ...raw,
    uid: raw.customer?.uid || raw.uid,
    userUid: raw.uid,
    customerUid: raw.customer?.uid || "",
    fullname: raw.customer?.name_th || raw.full_name || raw.email || "",
    email: raw.customer?.email || raw.email || "",
    phone: raw.customer?.phone || raw.phone || "",
    address: raw.customer?.company_address || "",
    avatar: raw.picture || null,
    memberSince: raw.created_at || null,
    isVerified: raw.is_verify,
  };
};

export const profileService = {
  get: async () => {
    const result = await http.get(ENDPOINTS.profile);
    if (!result.status) return getStoredProfile();
    const profile = mapProfile(result.data);
    setStoredProfile({ ...result.data, ...profile });
    return profile;
  },

  update: async (payload) => {
    const stored = getStoredProfile();
    const customerUid = stored?.customer?.uid || stored?.customerUid;
    if (!customerUid) throw new ApiError("ไม่พบข้อมูลลูกค้า");

    const result = await http.put(ENDPOINTS.updateCustomer(customerUid), {
      name_th: payload.fullname,
      name_en: payload.fullname,
      phone: payload.phone,
      email: payload.email,
      company_address: payload.address,
    });
    if (!result.status) throw new ApiError(result.message, result);

    const next = { ...stored, ...payload };
    setStoredProfile(next);
    window.dispatchEvent(new Event("lt-auth-change"));
    return next;
  },

  // Backend เก็บรหัสผ่านที่ตาราง users จึงต้องอัปเดตผ่าน v1/admin/users/:uid
  changePassword: async ({ password }) => {
    const stored = getStoredProfile();
    const userUid = stored?.uid || stored?.userUid;
    if (!userUid) throw new ApiError("ไม่พบข้อมูลผู้ใช้");

    const result = await http.put(`v1/admin/users/${userUid}`, {
      full_name: stored.full_name,
      phone: stored.phone,
      email: stored.email,
      role_id: stored.role?.uid,
      user_type: (stored.user_type || "").replaceAll(" ", "_"),
      password,
      is_active: stored.is_active,
      is_2fa: stored.is_2fa,
      is_mismatch_limit_approved: stored.is_mismatch_limit_approved,
    });
    if (!result.status) throw new ApiError(result.message, result);
    return result.data;
  },

  addresses: async () => {
    const stored = getStoredProfile();
    const customerUid = stored?.customer?.uid || stored?.customerUid;
    if (!customerUid) return [];
    const result = await http.get(ENDPOINTS.customerAddress, { uid: customerUid });
    if (!result.status) return [];
    const rows = Array.isArray(result.data) ? result.data : result.data?.address || [];
    return rows.filter((row) => row.is_active !== false);
  },

  // Backend เก็บที่อยู่เป็น address_list ก้อนเดียวบน customer จึงต้องส่งกลับทั้งชุดทุกครั้ง
  saveAddresses: async (rows) => {
    const stored = getStoredProfile();
    const customerUid = stored?.customer?.uid || stored?.customerUid;
    if (!customerUid) throw new ApiError("ไม่พบข้อมูลลูกค้า");

    const result = await http.put(ENDPOINTS.updateCustomer(customerUid), {
      uid: customerUid,
      address_list: rows.map((item) => ({
        ...(item.uid ? { uid: item.uid } : {}),
        contact_name: item.contact_name || "",
        contact_phone: item.contact_phone || "",
        contact_email: item.contact_email || "",
        address: item.address || "",
        sub_district: item.sub_district || "",
        district: item.district || "",
        province: item.province || "",
        zipcode: item.zipcode || "",
        country: item.country || "Thailand",
        is_default: !!item.is_default,
        remark: item.remark || "",
        is_active: item.is_active !== false,
      })),
    });
    if (!result.status) throw new ApiError(result.message, result);
    return rows;
  },
};

// --------------------------------------------------------------------------
// Catalog (mock)
// --------------------------------------------------------------------------
export const catalogService = {
  announcements: async () => mockResponse(M.ANNOUNCEMENTS),
  shortcuts: async () => mockResponse(M.HERO_SHORTCUTS),
  paymentChannels: async () => mockResponse(M.PAYMENT_CHANNELS),
  orderPaymentChannels: async () => mockResponse(M.ORDER_PAYMENT_CHANNELS),

  games: async (params = {}) => {
    if (!USE_MOCK_CATALOG) {
      const result = await http.get("v1/market/games", params);
      return result.status ? result.data : [];
    }
    let rows = M.GAMES;
    if (params.search) rows = rows.filter((g) => g.name.toLowerCase().includes(params.search.toLowerCase()));
    if (params.instant) rows = rows.filter((g) => g.instant);
    if (params.promo) rows = rows.filter((g) => g.promo);
    if (params.available) rows = rows.filter((g) => g.available);
    return mockResponse(rows);
  },
  game: async (id) => mockResponse(M.GAMES.find((g) => String(g.id) === String(id)) || null),

  mobileOperators: async () => mockResponse(M.MOBILE_OPERATORS),
  mobileOperator: async (id) => mockResponse(M.MOBILE_OPERATORS.find((o) => String(o.id) === String(id)) || null),

  cards: async () => mockResponse(M.CARDS),
  card: async (id) => mockResponse(M.CARDS.find((c) => String(c.id) === String(id)) || null),

  productCategories: async () => mockResponse(M.PRODUCT_CATEGORIES),
  products: async (params = {}) => {
    let rows = M.PRODUCTS;
    if (params.categoryId) rows = rows.filter((p) => String(p.categoryId) === String(params.categoryId));
    return mockResponse(rows);
  },
  product: async (id) => mockResponse(M.PRODUCTS.find((p) => String(p.id) === String(id)) || null),

  promotions: async () => mockResponse(M.PROMOTIONS),
  promotion: async (id) => mockResponse(M.PROMOTIONS.find((p) => String(p.id) === String(id)) || null),

  about: async () => mockResponse(M.ABOUT_CONTENT),
  policy: async () => mockResponse(M.POLICY_SECTIONS),
};

// --------------------------------------------------------------------------
// Payment - สร้าง QR ผ่าน transactions/deposits/partner
// --------------------------------------------------------------------------
const partnerCredentials = () => ({
  client_id: PARTNER.clientId,
  client_secret: PARTNER.clientSecret,
  client_ref_id: PARTNER.clientRefId,
  "x-apigw-api-id": PARTNER.apigwApiId,
});

const resolveCustomerId = () => {
  const stored = getStoredProfile();
  return stored?.customer?.uid || stored?.customerUid || PARTNER.customerIdDefault;
};

export const paymentService = {
  // items = [{ productName, productCode, productImage, quantity, price, orderType, detail }]
  createQrOrder: async ({ amount, items = [], remark = "", orderType }) => {
    const customerId = resolveCustomerId();
    if (!customerId) throw new ApiError("ไม่พบรหัสลูกค้า กรุณาเข้าสู่ระบบใหม่");

    const totalAmount = Number(Number(amount).toFixed(2));
    const body = {
      ...partnerCredentials(),
      customer_id: customerId,
      amount: totalAmount,
      ref_no: Date.now().toString(),
      currency: PARTNER.currency,
      order: {
        total_quantity: items.reduce((sum, item) => sum + (item.quantity || 1), 0),
        total_price: totalAmount.toFixed(2),
        fee: "0.00",
        order_status: "pending",
        remark,
        process_type: PROCESS_TYPE,
        order_items: items.map((item) => ({
          product_type: item.orderType || orderType,
          product_code: item.productCode || "",
          product_name: item.productName,
          product_image: item.productImage || "",
          quantity: item.quantity || 1,
          price: Number(Number(item.price).toFixed(2)),
          total_price: Number((Number(item.price) * (item.quantity || 1)).toFixed(2)),
          order_type: item.orderType || orderType,
          process_type: PROCESS_TYPE,
          // เก็บรายละเอียดเฉพาะของ LucidTopup (เกม/แพ็ก/ไอดีผู้เล่น) ไว้ใน remark
          remark: item.detail ? JSON.stringify(item.detail) : "",
        })),
      },
    };

    const result = await http.post(ENDPOINTS.depositPartner, body);
    if (!result.status) throw new ApiError(result.message, result);

    // deposits/partner ห่อ response ของ gateway ไว้อีกชั้น
    const gateway = result.data?.data || result.data;
    const gatewayMessage = result.data?.status?.message;
    if (gatewayMessage && gatewayMessage !== "Success") throw new ApiError(gatewayMessage, result);
    if (!gateway?.transaction_id) throw new ApiError("สร้างรายการชำระเงินไม่สำเร็จ", result);

    return {
      transactionId: gateway.transaction_id,
      refNo: gateway.ref_no,
      amount: Number(gateway.amount || totalAmount),
      base64: (gateway.base64 || "").replaceAll("_", "+"),
      orderDatetime: gateway.order_datetime,
      expireDatetime: gateway.expire_datetime,
    };
  },

  // อ่านสถานะ transaction (ใช้ poll ระหว่างรอผู้ใช้สแกนจ่าย)
  getTransaction: async (transactionId) => {
    const result = await http.get(ENDPOINTS.depositGetById, { uid: transactionId });
    if (!result.status) return null;
    const data = result.data;
    return {
      uid: data.uid,
      status: data.status,
      amount: Number(data.amount || 0),
      refNo: data.ref_no,
      base64: (data.qrs?.qr_base64 || "").replaceAll("_", "+"),
      expiredAt: data.qrs?.expired_at || null,
    };
  },

  // สั่งให้ Backend ไปถาม gateway ต้นทางว่ารายการจ่ายแล้วหรือยัง
  inquiry: async (transactionId) => http.get(ENDPOINTS.inquiry, { uid: transactionId }),

  // ผูกที่อยู่จัดส่งเข้ากับ order ที่เพิ่งสร้าง (ใช้กับการสั่งซื้อสินค้า)
  attachDelivery: async (transactionId, address) =>
    http.put(ENDPOINTS.orderDeliveryByTransaction(transactionId), {
      ...address,
      delivery_remark: address?.remark || "",
    }),
};

// --------------------------------------------------------------------------
// Orders / History
// --------------------------------------------------------------------------
const parseItemDetail = (item) => {
  try {
    return item?.remark ? JSON.parse(item.remark) : null;
  } catch (e) {
    return null;
  }
};

const mapOrder = (order) => {
  const item = order.order_items?.[0] || {};
  const detail = parseItemDetail(item);
  return {
    id: order.uid,
    code: order.order_no,
    status: order.transaction?.status || order.order_status,
    orderStatus: order.order_status,
    processType: order.process_type,
    orderType: item.order_type,
    createdAt: order.created_at,
    amount: Number(order.total_price || 0),
    quantity: item.quantity || order.total_quantity || 1,
    productName: item.product_name || "-",
    productImage: item.product_image || "",
    transactionUid: order.transaction?.uid || null,
    refNo: order.transaction?.ref_no || "",
    detail,
    raw: order,
  };
};

export const orderService = {
  list: async ({ page = 1, perPage = 10, orderType, search, startDate, endDate } = {}) => {
    const params = {
      page,
      page_size: perPage,
      process_type: PROCESS_TYPE,
      order_type: orderType,
      data_search: search,
      start_date: startDate,
      end_date: endDate,
    };
    const [result, count] = await Promise.all([
      http.get(ENDPOINTS.orders, params),
      http.get(ENDPOINTS.orderCounts, { ...params, page: undefined, page_size: undefined }),
    ]);

    if (!result.status) return { rows: [], total: 0, page, perPage };

    return {
      rows: (result.data || []).map(mapOrder),
      total: result.pagination?.count ?? count?.data?.total ?? (result.data || []).length,
      page,
      perPage,
    };
  },

  detail: async (uid) => {
    const result = await http.get(ENDPOINTS.orders, { uid });
    return result.status ? mapOrder(result.data) : null;
  },

  ORDER_TYPE,
};

// ประวัติแต่ละหน้าคือ order list ที่กรองด้วย order_type
export const historyService = {
  game: (params) => orderService.list({ ...params, orderType: ORDER_TYPE.game }),
  mobile: (params) => orderService.list({ ...params, orderType: ORDER_TYPE.mobile }),
  card: (params) => orderService.list({ ...params, orderType: ORDER_TYPE.card }),
  product: (params) => orderService.list({ ...params, orderType: ORDER_TYPE.product }),
  credit: (params) => orderService.list({ ...params, orderType: ORDER_TYPE.credit }),
};

export { TERMS_SUMMARY } from "./mockData";
export * from "./apiClient";
export * from "./config";
export { cartService } from "./cart";
